/**
 * Client-side validation for the register front-door (uid-001-3b).
 *
 * These rules MIRROR the identity-service server rules so a person gets inline
 * feedback BEFORE submit — they are NOT the authority. The server
 * (`uid-001-4c` `validate_password`, `uid-001-4` DOB/age, `uid-001-2` email)
 * remains the source of truth and re-validates everything. In particular:
 *   - Age is computed SERVER-SIDE only — we never gate on age client-side
 *     (COPPA 403/501 stays server-authoritative).
 *
 * Password rules are byte-for-byte parity with identity-service
 * `src/password.rs::validate_password`:
 *   - length >= 12 and <= 128
 *   - >= 1 number, >= 1 uppercase, >= 1 lowercase, >= 1 special (non-alphanumeric)
 */

export const MIN_PASSWORD_LENGTH = 12;
export const MAX_PASSWORD_LENGTH = 128;

/**
 * Pragmatic email shape check. Matches a non-empty local part, an `@`, a domain
 * with at least one dot, and no whitespace. The server normalizes + does the
 * authoritative check; this is just pre-submit feedback.
 */
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** `YYYY-MM-DD` with a real calendar-valid date. */
const DOB_RE = /^\d{4}-\d{2}-\d{2}$/;

export function validateEmail(email: string): string | null {
  if (!email) {
    return 'Email is required';
  }
  if (!EMAIL_RE.test(email)) {
    return 'Enter a valid email address';
  }
  return null;
}

/**
 * Mirror of server `validate_password`. Returns the FIRST failing reason (same
 * order as the server) or null when valid. Length uses the UTF-8 byte length
 * to match the server's `password.len()` (bytes), so a multi-byte password is
 * measured the same way on both sides.
 */
export function validatePassword(password: string): string | null {
  if (!password) {
    return 'Password is required';
  }
  const byteLen = new TextEncoder().encode(password).length;
  if (byteLen < MIN_PASSWORD_LENGTH) {
    return `Use at least ${MIN_PASSWORD_LENGTH} characters`;
  }
  if (byteLen > MAX_PASSWORD_LENGTH) {
    return `Password must not exceed ${MAX_PASSWORD_LENGTH} characters`;
  }
  if (!/[0-9]/.test(password)) {
    return 'Include at least one number';
  }
  if (!/\p{Lu}/u.test(password)) {
    return 'Include at least one uppercase letter';
  }
  if (!/\p{Ll}/u.test(password)) {
    return 'Include at least one lowercase letter';
  }
  // Special = any non-alphanumeric character (matches server `!is_alphanumeric`).
  if (!/[^\p{L}\p{N}]/u.test(password)) {
    return 'Include at least one special character';
  }
  return null;
}

export function validateConfirmPassword(
  password: string,
  confirm: string,
): string | null {
  if (!confirm) {
    return 'Please confirm your password';
  }
  if (password !== confirm) {
    return "These passwords don't match";
  }
  return null;
}

/**
 * DOB must be present, `YYYY-MM-DD`, and a real calendar date (no 2026-02-30).
 * We do NOT compute age here — age classification is server-side (COPPA).
 */
export function validateDob(dob: string): string | null {
  if (!dob) {
    return 'Date of birth is required';
  }
  if (!DOB_RE.test(dob)) {
    return 'Date of birth must be in YYYY-MM-DD format';
  }
  const [y, m, d] = dob.split('-').map(Number);
  const parsed = new Date(Date.UTC(y, m - 1, d));
  if (
    parsed.getUTCFullYear() !== y ||
    parsed.getUTCMonth() !== m - 1 ||
    parsed.getUTCDate() !== d
  ) {
    return 'Enter a valid date of birth';
  }
  return null;
}

export interface RegisterFieldErrors {
  email?: string;
  password?: string;
  confirmPassword?: string;
  dob?: string;
}

export interface RegisterFormValues {
  email: string;
  password: string;
  confirmPassword: string;
  dob: string;
  displayName: string;
}

/** Run every field validator; returns only the fields that failed. */
export function validateRegisterForm(
  values: RegisterFormValues,
): RegisterFieldErrors {
  const errors: RegisterFieldErrors = {};
  const email = validateEmail(values.email);
  if (email) errors.email = email;
  const password = validatePassword(values.password);
  if (password) errors.password = password;
  const confirmPassword = validateConfirmPassword(
    values.password,
    values.confirmPassword,
  );
  if (confirmPassword) errors.confirmPassword = confirmPassword;
  const dob = validateDob(values.dob);
  if (dob) errors.dob = dob;
  return errors;
}
